import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, Database, Settings as SettingsIcon, Search, AlertCircle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function AccountCodeSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('customers'); // 'customers' or 'service-groups'
  
  // FIPS Customers State
  const [customers, setCustomers] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersSearch, setCustomersSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    customer: '',
    clean_customer: '',
    state: '',
    entity_type: '',
    fips: '',
    gw_acc_id: null
  });
  const [confirmDeleteCustomerId, setConfirmDeleteCustomerId] = useState(null);
  
  // Service Groups State
  const [serviceGroups, setServiceGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({
    group_key: '',
    code: '',
    name: '',
    short_code: '',
    sub_services: []
  });
  const [confirmDeleteGroupKey, setConfirmDeleteGroupKey] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab, customersSearch, stateFilter, entityTypeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'customers') {
        const res = await api.get('/admin/account-codes/customers', {
          params: { 
            search: customersSearch || undefined, 
            state: stateFilter || undefined,
            entity_type: entityTypeFilter || undefined,
            limit: 100 
          }
        });
        setCustomers(res.data.customers || []);
        setCustomersTotal(res.data.total || 0);
      } else {
        const res = await api.get('/admin/account-codes/service-groups');
        setServiceGroups(res.data.service_groups || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // CUSTOMERS HANDLERS
  // ===========================

  const handleAddCustomer = async () => {
    if (!newCustomer.customer.trim() || !newCustomer.fips.trim()) {
      toast.error('Customer name and FIPS code are required');
      return;
    }

    try {
      await api.post('/admin/account-codes/customers', newCustomer);
      toast.success('FIPS customer added successfully');
      setShowAddCustomer(false);
      setNewCustomer({
        customer: '',
        clean_customer: '',
        state: '',
        entity_type: '',
        fips: '',
        gw_acc_id: null
      });
      loadData();
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer || !editingCustomer.customer.trim() || !editingCustomer.fips.trim()) {
      toast.error('Customer name and FIPS code are required');
      return;
    }

    try {
      await api.put(`/admin/account-codes/customers/${editingCustomer.id}`, editingCustomer);
      toast.success('FIPS customer updated successfully');
      setEditingCustomer(null);
      loadData();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      await api.delete(`/admin/account-codes/customers/${customerId}`);
      toast.success('FIPS customer deleted successfully');
      setConfirmDeleteCustomerId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  // ===========================
  // SERVICE GROUPS HANDLERS
  // ===========================

  const handleAddGroup = async () => {
    if (!newGroup.group_key.trim() || !newGroup.name.trim()) {
      toast.error('Group key and name are required');
      return;
    }

    try {
      await api.post('/admin/account-codes/service-groups', newGroup);
      toast.success('Service group added successfully');
      setShowAddGroup(false);
      setNewGroup({
        group_key: '',
        code: '',
        name: '',
        short_code: '',
        sub_services: []
      });
      loadData();
    } catch (error) {
      console.error('Error adding service group:', error);
      toast.error(error.response?.data?.detail || 'Failed to add service group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editingGroup.group_key.trim() || !editingGroup.name.trim()) {
      toast.error('Group key and name are required');
      return;
    }

    try {
      await api.put(`/admin/account-codes/service-groups/${editingGroup.group_key}`, editingGroup);
      toast.success('Service group updated successfully');
      setEditingGroup(null);
      loadData();
    } catch (error) {
      console.error('Error updating service group:', error);
      toast.error(error.response?.data?.detail || 'Failed to update service group');
    }
  };

  const handleDeleteGroup = async (groupKey) => {
    try {
      await api.delete(`/admin/account-codes/service-groups/${groupKey}`);
      toast.success('Service group deleted successfully');
      setConfirmDeleteGroupKey(null);
      loadData();
    } catch (error) {
      console.error('Error deleting service group:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete service group');
    }
  };

  const addSubService = (groupState, setGroupState) => {
    setGroupState({
      ...groupState,
      sub_services: [
        ...groupState.sub_services,
        { code: '', name: '', description: '' }
      ]
    });
  };

  const removeSubService = (groupState, setGroupState, index) => {
    setGroupState({
      ...groupState,
      sub_services: groupState.sub_services.filter((_, i) => i !== index)
    });
  };

  const updateSubService = (groupState, setGroupState, index, field, value) => {
    const updated = [...groupState.sub_services];
    updated[index] = { ...updated[index], [field]: value };
    setGroupState({
      ...groupState,
      sub_services: updated
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <Database className="w-6 h-6 text-indigo-600" />
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Account Code Settings</h1>
                  <p className="text-sm text-slate-600">Manage FIPS customer mappings and service subdimensions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('customers')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'customers'
                ? 'bg-white text-slate-900 shadow'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            FIPS Customer Mappings
          </button>
          <button
            onClick={() => setActiveTab('service-groups')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'service-groups'
                ? 'bg-white text-slate-900 shadow'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Service Subdimensions
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        {activeTab === 'customers' ? (
          <div className="card p-6">
            {/* Info Banner - CSV Source */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <Database className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-blue-900">Viewing CSV Data Source</div>
                <div className="text-sm text-blue-700 mt-0.5">
                  Displaying all <strong>36,034 entities</strong> from gw_entity_mapping.csv. 
                  This is a read-only view of the definitive entity mapping source covering all 50 states + DC.
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex items-start gap-3 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={customersSearch}
                  onChange={(e) => setCustomersSearch(e.target.value)}
                  placeholder="Search by entity name, state, or FIPS..."
                  className="input-field pl-10 w-full"
                />
              </div>
              
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All States</option>
                <option value="AL">Alabama</option>
                <option value="GA">Georgia</option>
                <option value="MO">Missouri</option>
                <option value="TX">Texas</option>
                <option value="CA">California</option>
                <option value="FL">Florida</option>
                <option value="NY">New York</option>
                {/* Add more states as needed */}
              </select>

              <select
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Types</option>
                <option value="MU">Municipality (MU)</option>
                <option value="CO">County (CO)</option>
                <option value="SC">School (SC)</option>
                <option value="ST">State (ST)</option>
                <option value="OT">Other (OT)</option>
              </select>
            </div>

            {/* Note: Add/Edit/Delete disabled for CSV view */}
            <div className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded border border-slate-200">
              <strong>Note:</strong> This is a read-only view of the CSV data. 
              To modify entities, update the gw_entity_mapping.csv file directly.
            </div>

            {/* Customers List (Read-Only CSV View) */}
            <div className="space-y-2">
              {loading ? (
                <p className="text-slate-500 text-center py-8">Loading...</p>
              ) : customers.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No customers found</p>
                </div>
              ) : (
                customers.map((customer) => (
                  <div key={customer.id} className="p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{customer.clean_customer}</div>
                        <div className="text-sm text-slate-600 mt-1 space-y-0.5">
                          <div>
                            <span className="inline-block mr-4">
                              <strong>FIPS Code:</strong> 
                              <code className="ml-1 bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono">
                                {customer.fips}
                              </code>
                            </span>
                            <span className="inline-block mr-4">
                              <strong>State:</strong> {customer.state}
                            </span>
                            <span className="inline-block mr-4">
                              <strong>Type:</strong> 
                              <span className="ml-1 font-mono">{customer.entity_type}</span>
                              <span className="text-slate-400 ml-1">
                                ({customer.entity_type === 'MU' ? 'Municipality' : 
                                  customer.entity_type === 'CO' ? 'County' : 
                                  customer.entity_type === 'SC' ? 'School' : 
                                  customer.entity_type === 'ST' ? 'State' : 'Other'})
                              </span>
                            </span>
                          </div>
                          {customer.district_type && (
                            <div>
                              <strong>District Type:</strong> {customer.district_type}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {customersTotal > 0 && (
              <div className="mt-4 text-sm text-slate-600 text-center">
                Showing {customers.length} of {customersTotal} customers
              </div>
            )}
          </div>
        ) : (
          <div className="card p-6">
            {/* Add Button */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Service Groups & Subdimensions</h2>
              <button
                onClick={() => setShowAddGroup(!showAddGroup)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Service Group
              </button>
            </div>

            {/* Add New Group Form */}
            {showAddGroup && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-medium text-slate-900 mb-3">Add New Service Group</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    value={newGroup.group_key}
                    onChange={(e) => setNewGroup({ ...newGroup, group_key: e.target.value.toUpperCase() })}
                    placeholder="Group Key (e.g., TAXES)"
                    className="input-field"
                  />
                  <input
                    type="text"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    placeholder="Group Name (e.g., Taxes)"
                    className="input-field"
                  />
                  <input
                    type="text"
                    value={newGroup.code}
                    onChange={(e) => setNewGroup({ ...newGroup, code: e.target.value })}
                    placeholder="Base Code (e.g., 1000)"
                    className="input-field"
                  />
                  <input
                    type="text"
                    value={newGroup.short_code}
                    onChange={(e) => setNewGroup({ ...newGroup, short_code: e.target.value })}
                    placeholder="Short Code (e.g., TAX)"
                    className="input-field"
                  />
                </div>

                {/* Sub-Services */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Sub-Services</label>
                    <button
                      onClick={() => addSubService(newGroup, setNewGroup)}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      + Add Sub-Service
                    </button>
                  </div>
                  <div className="space-y-2">
                    {newGroup.sub_services.map((sub, index) => (
                      <div key={index} className="grid grid-cols-3 gap-2 items-start">
                        <input
                          type="text"
                          value={sub.code}
                          onChange={(e) => updateSubService(newGroup, setNewGroup, index, 'code', e.target.value)}
                          placeholder="Code (e.g., 1001)"
                          className="input-field text-sm"
                        />
                        <input
                          type="text"
                          value={sub.name}
                          onChange={(e) => updateSubService(newGroup, setNewGroup, index, 'name', e.target.value)}
                          placeholder="Name (e.g., Income Tax)"
                          className="input-field text-sm"
                        />
                        <div className="flex space-x-1">
                          <input
                            type="text"
                            value={sub.description}
                            onChange={(e) => updateSubService(newGroup, setNewGroup, index, 'description', e.target.value)}
                            placeholder="Description"
                            className="input-field text-sm flex-1"
                          />
                          <button
                            onClick={() => removeSubService(newGroup, setNewGroup, index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button onClick={handleAddGroup} className="btn-primary">
                    <Check className="w-4 h-4 mr-2" />
                    Add Group
                  </button>
                  <button onClick={() => setShowAddGroup(false)} className="btn-secondary">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Service Groups List */}
            <div className="space-y-4">
              {loading ? (
                <p className="text-slate-500 text-center py-8">Loading...</p>
              ) : serviceGroups.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No service groups found</p>
                </div>
              ) : (
                serviceGroups.map((group) => (
                  <div key={group.group_key} className="p-4 bg-white rounded-lg border border-slate-200">
                    {editingGroup?.group_key === group.group_key ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={editingGroup.group_key}
                            onChange={(e) => setEditingGroup({ ...editingGroup, group_key: e.target.value.toUpperCase() })}
                            className="input-field"
                            placeholder="Group Key"
                            disabled
                          />
                          <input
                            type="text"
                            value={editingGroup.name}
                            onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                            className="input-field"
                            placeholder="Group Name"
                          />
                          <input
                            type="text"
                            value={editingGroup.code}
                            onChange={(e) => setEditingGroup({ ...editingGroup, code: e.target.value })}
                            className="input-field"
                            placeholder="Base Code"
                          />
                          <input
                            type="text"
                            value={editingGroup.short_code}
                            onChange={(e) => setEditingGroup({ ...editingGroup, short_code: e.target.value })}
                            className="input-field"
                            placeholder="Short Code"
                          />
                        </div>

                        {/* Sub-Services */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-slate-700">Sub-Services</label>
                            <button
                              onClick={() => addSubService(editingGroup, setEditingGroup)}
                              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              + Add Sub-Service
                            </button>
                          </div>
                          <div className="space-y-2">
                            {editingGroup.sub_services.map((sub, index) => (
                              <div key={index} className="grid grid-cols-3 gap-2 items-start">
                                <input
                                  type="text"
                                  value={sub.code}
                                  onChange={(e) => updateSubService(editingGroup, setEditingGroup, index, 'code', e.target.value)}
                                  placeholder="Code"
                                  className="input-field text-sm"
                                />
                                <input
                                  type="text"
                                  value={sub.name}
                                  onChange={(e) => updateSubService(editingGroup, setEditingGroup, index, 'name', e.target.value)}
                                  placeholder="Name"
                                  className="input-field text-sm"
                                />
                                <div className="flex space-x-1">
                                  <input
                                    type="text"
                                    value={sub.description}
                                    onChange={(e) => updateSubService(editingGroup, setEditingGroup, index, 'description', e.target.value)}
                                    placeholder="Description"
                                    className="input-field text-sm flex-1"
                                  />
                                  <button
                                    onClick={() => removeSubService(editingGroup, setEditingGroup, index)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <button onClick={handleUpdateGroup} className="btn-primary flex-1">
                            <Check className="w-4 h-4 mr-2" />
                            Save
                          </button>
                          <button onClick={() => setEditingGroup(null)} className="btn-secondary flex-1">
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-lg text-slate-900">{group.name}</span>
                              <span className="text-sm text-slate-500">({group.group_key})</span>
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                              <span className="inline-block mr-4">
                                <strong>Base Code:</strong> {group.code}
                              </span>
                              <span className="inline-block">
                                <strong>Short Code:</strong> {group.short_code}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setEditingGroup(group)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setConfirmDeleteGroupKey(group.group_key)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            {confirmDeleteGroupKey === group.group_key && (
                              <div className="flex items-center space-x-1 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                                <span className="text-xs text-red-700 font-medium">Delete?</span>
                                <button
                                  onClick={() => handleDeleteGroup(group.group_key)}
                                  className="p-1 text-white bg-red-600 hover:bg-red-700 rounded text-xs font-bold"
                                >Yes</button>
                                <button
                                  onClick={() => setConfirmDeleteGroupKey(null)}
                                  className="p-1 text-slate-600 hover:bg-slate-100 rounded text-xs"
                                >No</button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Sub-Services Display */}
                        {group.sub_services && group.sub_services.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="text-xs font-medium text-slate-600 mb-2">Sub-Services ({group.sub_services.length})</div>
                            <div className="grid grid-cols-2 gap-2">
                              {group.sub_services.map((sub, idx) => (
                                <div key={idx} className="text-sm bg-slate-50 p-2 rounded">
                                  <div className="font-medium text-slate-800">
                                    {sub.name} <span className="text-slate-500">({sub.code})</span>
                                  </div>
                                  {sub.description && (
                                    <div className="text-xs text-slate-600 mt-0.5">{sub.description}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
